import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as _ from 'lodash';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { ListItem } from '../index';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { PMenuUtils } from '../../p-menu';

type OutputField<T> = keyof T | 'entireObject' | string;
type WithId = { id: number | string };

@Component({
    selector: 'p-picker-multi-select',
    template: `
        <div class="p-menu-container" (click)="$event.stopPropagation()" (keydown.enter)="selectCompleted(this.dirty)">
            <div
                class="p-menu-header"
                *ngIf="displayHeader"
                [ngClass]="{
                    extended: enableClear && enableNegate && !hasHiddenValues,
                    'half-extended': (enableClear && !enableNegate) || actions || enableAddAll,
                    'multi-extended': enableClear && enableNegate && hasHiddenValues
                }"
            >
                <p-field-single-select
                    *ngIf="actions"
                    class="p-menu-submenu"
                    [list]="actions"
                    [value]="selectedAction"
                    (valueChange)="actionsChanged($event)"
                >
                </p-field-single-select>
                <div [ngClass]="{ 'multi-filter': useMultiMode }">
                    <p-field-outline-text
                        class="p-menu-input"
                        *ngIf="enableSearch"
                        [isSearch]="true"
                        [autoFocus]="true"
                        (search)="touch($event)"
                        (clearSearch)="touch($event)"
                        (keydown.arrowdown)="focusElemList()"
                        (keydown.enter)="toggleLoneSearchElem()"
                    ></p-field-outline-text>
                    <p-icon
                        *ngIf="useMultiMode"
                        name="paste-to"
                        (click)="pasteModeClicked()"
                        class="change-mode"
                        [ngClass]="{ 'disable-icon': multiModeDisabled }"
                        [matTooltip]="
                            multiModeDisabled
                                ? ('gridFilters.tooltip.pasteMultipleDisabled' | translate)
                                : ('gridFilters.tooltip.pasteMultiple' | translate)
                        "
                    ></p-icon>
                </div>
                <p-button
                    *ngIf="enableClear"
                    class="p-menu-btn-line"
                    (buttonClicked)="clearAll()"
                    label="{{ 'Actions.clearSelected' | translate }}"
                    [disabled]="noneSelected"
                    iconBefore="eraser"
                    iconBeforeSize="sm"
                    [buttonType]="buttonType.Tertiary"
                ></p-button>
                <p-checkbox
                    (checkedChange)="toggleNegate($event)"
                    *ngIf="enableNegate"
                    class="p-menu-btn-line p-checkbox-align"
                    label="{{ 'Actions.notEqualTo' | translate }}"
                    [attr.automation-id]="'not-equal-to'"
                    [checked]="negate"
                    [disabled]="noneSelected"
                ></p-checkbox>
                <p-checkbox
                    (checkedChange)="toggleHiddenValues($event)"
                    *ngIf="hasHiddenValues"
                    class="p-menu-btn-line p-checkbox-align"
                    label="{{ 'Actions.showHidden' | translate }}"
                    [attr.automation-id]="'show-hidden'"
                    [checked]="showHiddenValues"
                ></p-checkbox>
                <p-checkbox
                    (checkedChange)="toggleAddAll($event)"
                    *ngIf="enableAddAll"
                    label="{{ 'Actions.selectAll' | translate }}"
                    class="p-menu-btn-line p-checkbox-align"
                    [attr.automation-id]="'add-all'"
                    [checked]="addAll"
                ></p-checkbox>
            </div>
            <div class="p-menu-body multi-select">
                <mat-selection-list class="p-menu-body-container" #optionsList="matSelectionList">
                    <cdk-virtual-scroll-viewport
                        [style.height.px]="getHeight()"
                        itemSize="32"
                        minBufferPx="320"
                        maxBufferPx="320"
                        class="select-viewport"
                    >
                        <mat-list-option
                            class="p-menu-multiple-item"
                            *ngIf="emptyOption"
                            togglePosition="before"
                            [selected]="emptyOption | extract: selectedPredicate:selectedValues"
                            [disableRipple]="true"
                            [value]="emptyOption[idField]"
                            [disabled]="isDisabled(emptyOption)"
                            (click)="toggleValue(emptyOption[idField])"
                            (keydown.space)="toggleValue(emptyOption[idField]); $event.stopImmediatePropagation()"
                            (keydown.enter)="toggleValue(emptyOption[idField]); $event.stopImmediatePropagation()"
                        >
                            {{ emptyOption[nameField] }}
                        </mat-list-option>
                        <ng-container
                            *cdkVirtualFor="
                                let item of list
                                    | filter: isVisiblePredicate:{ showHiddenValues: this.showHiddenValues }
                                    | filter: searchPredicate:searchCriteria;
                                index as i
                            "
                        >
                            <span
                                *ngIf="shouldDisplayTitle(list | filter: searchPredicate:searchCriteria, i)"
                                class="p-menu-group-title p-headline-6"
                            >
                                {{ item[groupField] }}
                            </span>
                            <mat-list-option
                                [attr.automation-id]="getAttrAutomationId(item[nameField])"
                                class="p-menu-multiple-item"
                                togglePosition="before"
                                [disableRipple]="true"
                                [disabled]="isDisabled(item)"
                                [value]="item[idField]"
                                [class.selected-checkbox]="item | extract: selectedPredicate:selectedValues"
                                [selected]="item | extract: selectedPredicate:selectedValues"
                                (click)="toggleValue(item[idField]); $event.preventDefault(); $event.stopImmediatePropagation()"
                                (keydown.space)="toggleValue(item[idField]); $event.stopImmediatePropagation()"
                                (keydown.enter)="toggleValue(item[idField]); $event.stopImmediatePropagation()"
                            >
                                <span [matTooltip]="item[nameField]" [showOnlyWhenEllipsis]="true" class="p-menu-item-text">{{
                                    item[nameField]
                                }}</span>
                                <p-icon
                                    *ngIf="item.iconName"
                                    class="p-menu-item-icon"
                                    [name]="item.iconName"
                                    [matTooltip]="item.tooltipText"
                                    matTooltipPosition="above"
                                ></p-icon>
                            </mat-list-option>
                        </ng-container>
                        <mat-list-option
                            class="p-menu-multiple-item"
                            *ngIf="enableAddNew && searchCriteria?.length && !(list | filter: searchExisted:searchCriteria)?.length"
                            togglePosition="before"
                            [disableRipple]="true"
                            [selected]="searchCriteria | extract: selectedPredicate:selectedValues"
                            [value]="searchCriteria"
                            [disabled]="newItemValidation?.test(searchCriteria) === false"
                            (click)="addNewValue(searchCriteria); $event.preventDefault(); $event.stopImmediatePropagation()"
                            (keydown.space)="addNewValue(searchCriteria)"
                            (keydown.enter)="addNewValue(searchCriteria)"
                        >
                            <span class="p-menu-item-text">{{ searchCriteria }}{{ newItemText | translate }}</span>
                        </mat-list-option>
                        <div class="p-menu-empty" *ngIf="!enableAddNew && !(list | filter: searchPredicate:searchCriteria)?.length">
                            <p class="p-menu-empty-state p-paragraph-1">
                                {{ noData }}
                            </p>
                        </div>
                    </cdk-virtual-scroll-viewport>
                </mat-selection-list>
            </div>
            <div class="p-menu-footer">
                <p-button
                    [disabled]="!dirty"
                    class="p-menu-footer-btn"
                    label="{{ 'Actions.done' | translate }}"
                    (click)="selectCompleted(dirty)"
                ></p-button>
                <p-button
                    class="p-menu-footer-btn"
                    [buttonType]="buttonType.Secondary"
                    label="{{ 'Actions.cancel' | translate }}"
                    (click)="selectCanceled()"
                ></p-button>
            </div>
        </div>
    `,
    styleUrls: ['./p-picker-multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PPickerMultiSelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pPickerMultiSelect'
})
export class PPickerMultiSelectComponent<T extends number | string | WithId> implements AfterViewInit, AfterContentInit, OnDestroy {
    searchCriteria = '';
    singleSearchElem: any;
    dirty = false;
    menuOpen = false;
    pMenuUtils = new PMenuUtils();

    @HostBinding('class.ag-custom-component-popup') popup = true;
    @Input() selectedValues: T[] = [];
    @Input() list: any[] = [];
    @Input() id?: string | number;
    @Input() isMandatory = false;
    @Input() customClasses?: string;
    @Input() isInEditMode = true;
    // May implement this or not according to need;
    @Input() placeholder?: string;
    @Input() idField = 'id';
    @Input() outputField: OutputField<T> = this.idField;
    @Input() nameField = 'name';
    @Input() groupField = '';
    @Input() emptyOption: any;
    @Input() useFullScreenHeight = false;
    @Input() noData = 'No data found';
    @Input() autoFocus = false;
    @Input() enableClear = false;
    @Input() enableNegate = false;
    @Input() enableAddAll = false;
    @Input() enableSearch = true;
    @Input() enableAddNew = false;
    @Input() newItemValidation = /.*/;
    @Input() newItemText = 'Actions.addNewSelectOption';
    @Input() keepListOrder = false;
    @Input() negate = false;
    @Input() addAll = false;
    @Input() actions?: ListItem[];
    @Input() selectedAction?: number;
    @Input() disableForEmptyOption: boolean = false;

    @Input() multiple = false;
    @Input() useMultiMode = false;
    @Input() multiModeDisabled?: boolean;

    @Output() negateChange = new EventEmitter<boolean>();
    @Output() searchChanged = new EventEmitter<string>();
    @Output() cancel = new EventEmitter<string>();
    @Output() apply = new EventEmitter<T[]>();
    @Output() addNew = new EventEmitter<string>();
    @Output() actionChanged = new EventEmitter<number>();
    @Output() changeFilterMode = new EventEmitter<void>();

    @ViewChild(CdkVirtualScrollViewport, { static: false }) viewPort: CdkVirtualScrollViewport = {} as CdkVirtualScrollViewport;
    @ViewChild('optionsList') matSelectionList: MatSelectionList = {} as MatSelectionList;

    buttonType = ButtonTypeEnum;
    numOfDisplayedItems?: number;
    hasHiddenValues = false;
    showHiddenValues = false;

    constructor() {}

    get displayHeader(): boolean {
        return this.enableSearch || this.enableClear || this.enableNegate;
    }

    ngAfterViewInit(): void {
        this.selectedValues = Object.assign([], this.selectedValues);
        const hiddenItemsCount = this.list.filter((item) => item.hidden).length;
        this.numOfDisplayedItems = this.list.length - hiddenItemsCount;
        this.focusSearchBox();

        if (this.addAll) {
            this.toggleAddAll(true);
        }
        this.handleHiddenValues(hiddenItemsCount);
    }

    private handleHiddenValues(hiddenItemsCount: number) {
        this.hasHiddenValues = !!hiddenItemsCount;
        this.list = this.list.map((item) => ({
            ...item,
            ...(item.hidden ? { iconName: '12p-hide' } : {})
        }));
    }

    ngAfterContentInit(): void {
        this.focusSearchBox();
    }

    isDisabled(item: any): boolean {
        if (!this.disableForEmptyOption || !this.emptyOption) {
            return false;
        }

        if (this.emptyOption && item[this.idField] == this.emptyOption[this.idField]) {
            return !!this.selectedValues && !!this.selectedValues.length && !this.selectedValues.includes(this.emptyOption[this.idField]);
        }
        return !this.selectedValues ? true : this.selectedValues.includes(this.emptyOption?.[this.idField]);
    }

    focusSearchBox(): void {
        // if (this.autoFocus) {
        // this.searchbox?.nativeElement?.focus();
        // }
    }

    getHeight(): number {
        let numOfItem =
            this.numOfDisplayedItems === null || this.numOfDisplayedItems === undefined ? this.list.length : this.numOfDisplayedItems;
        numOfItem = numOfItem === 0 && this.enableAddNew ? 1 : numOfItem;
        const numOfGroups = this.groupField ? Object.keys(_.groupBy(this.list, this.groupField)).length : 0;
        return this.pMenuUtils.calcHeight(numOfGroups, numOfItem, this.emptyOption, this.useFullScreenHeight);
    }

    getAttrAutomationId(itemName: any): string {
        return 'column-' + itemName.replace(/ /g, '-');
    }

    selectCompleted(dirty: boolean): void {
        if (dirty) {
            this.apply.emit(this.selectedValues);
        }
    }

    selectCanceled(): void {
        this.cancel.emit();
    }

    openChange(ev: any): void {
        if (ev) {
            this.viewPort.scrollToIndex(0);
            this.viewPort.checkViewportSize();
        }
    }

    isVisiblePredicate =
        (param: { showHiddenValues: boolean }) =>
        (item: any): boolean =>
            param.showHiddenValues || !item.hidden;

    selectedPredicate =
        (value: any) =>
        (item: any): boolean =>
            value.some((selectedItem: WithId | string | number) =>
                typeof selectedItem === 'object'
                    ? selectedItem[this.idField as keyof WithId] == item[this.idField]
                    : selectedItem == item[this.idField]
            );

    searchPredicate =
        (substring: string) =>
        (item: any): boolean =>
            item[this.idField] === this.emptyOption?.[this.idField] ||
            _.toLower(item[this.nameField]).includes(_.trim(_.toLower(substring)));

    searchExisted =
        (substring: string) =>
        (item: any): boolean =>
            _.toLower(item[this.nameField]) === _.trim(_.toLower(substring));

    get noneSelected(): boolean {
        return _.isEmpty(this.selectedValues);
    }

    pasteModeClicked(): void {
        if (!this.multiModeDisabled) this.changeFilterMode.emit();
    }

    clearAll(): void {
        this.dirty = true;
        this.negate = false;
        this.selectedValues = [];
        this.negateChange.emit(this.negate);
        this.setOptionsSelected(false);
    }

    setOptionsSelected(value: boolean): void {
        this.matSelectionList.options.map((data: MatListOption) => (data.selected = value));
    }

    toggleAddAll(checked: boolean): void {
        this.dirty = true;
        this.addAll = checked;
        this.matSelectionList.options.map((data: MatListOption) => {
            data.selected = checked;
        });
        this.selectedValues = checked ? this.list.map((value) => value[this.idField]) : [];
    }

    actionsChanged(e: any): void {
        this.actionChanged.emit(e);
        this.dirty = true;
    }

    touch(value: any): void {
        this.searchCriteria = value;
        const filteredList = this.list.filter(
            (entity: any) => !this.isHidden(entity) && _.toLower(entity[this.nameField]).includes(_.trim(_.toLower(value)))
        );
        this.numOfDisplayedItems = filteredList.length;
        if (this.isNewValue(value, filteredList)) {
            this.numOfDisplayedItems++;
        }
        this.singleSearchElem = this.numOfDisplayedItems === 1 ? filteredList[0] : null;
        this.searchChanged.emit(this.searchCriteria);
    }

    private isHidden(item: any): boolean {
        return !this.showHiddenValues && item.hidden;
    }

    private isNewValue(value: any, filteredList: any[]): boolean {
        return this.enableAddNew && value.length > 0 && filteredList.length > 0 && !filteredList.map((item) => item.name).includes(value);
    }

    toggleValue(val: any): void {
        this.dirty = true;
        // should be only == and not === for ID column
        if (_.findIndex(this.selectedValues, (v: any) => (typeof v === 'object' ? v.id == val : v == val)) === -1) {
            this.selectedValues = [...this.selectedValues, val];
        } else {
            this.selectedValues = _.reject(this.selectedValues, (v: any) => (typeof v === 'object' ? v.id == val : v == val));

            if (this.noneSelected) {
                this.negate = false;
                this.negateChange.emit(this.negate);
            }
        }

        if (this.keepListOrder) {
            const addEmptyOption = this.selectedValues.includes(this.emptyOption?.[this.idField]);
            if (this.outputField == 'entireObject') {
                this.selectedValues = this.list.filter(
                    (item) =>
                        this.selectedValues.find((selectedValue: number | WithId | string) => {
                            if (typeof selectedValue === 'number' || typeof selectedValue === 'string') {
                                return selectedValue === item.id;
                            } else {
                                return selectedValue?.id === item.id;
                            }
                        }) !== undefined
                );
            } else {
                this.selectedValues = this.list
                    .filter(
                        (item: any) => _.find(this.selectedValues, (selectedValueId: number) => selectedValueId == item.id) != undefined
                    )
                    .map((item: any) => item.id);
            }
            if (addEmptyOption) {
                this.selectedValues = [this.emptyOption?.[this.idField], ...this.selectedValues];
            }
        }

        this.addAll = this.selectedValues?.length === this.list?.length;
    }

    toggleNegate(checked: boolean): void {
        this.dirty = true;
        this.negate = checked;
        this.negateChange.emit(this.negate);
    }

    toggleHiddenValues(checked: boolean): void {
        this.showHiddenValues = checked;
        this.touch(this.searchCriteria); //resize element
    }

    focusOptions(optionsList: MatSelectionList): void {
        optionsList?.focus();
    }

    shouldDisplayTitle(filtered: any, index: number): boolean {
        if (this.groupField && filtered && filtered.length > 0) {
            if (index === 0) {
                return true;
            }
            if (filtered[index][this.groupField] !== filtered[index - 1][this.groupField]) {
                return true;
            }
        }
        return false;
    }

    addNewValue(value: string): void {
        if (this.newItemValidation && !value.match(this.newItemValidation)) {
            return;
        }
        this.addNew.emit(value);
        this.toggleValue(value);
    }

    ngOnDestroy(): void {
        // Emit empty search in order to reset the list of values in case of server search
        if (this.enableSearch) {
            this.searchChanged.emit('');
        }
    }

    focusElemList(): void {
        this.focusOptions(this.matSelectionList);
    }

    toggleLoneSearchElem(): void {
        if (this.numOfDisplayedItems === 1) {
            this.toggleValue(this.singleSearchElem[this.idField]);
        }
    }
}
